// hooks/usePaginatedList.ts
import { useInfiniteQuery } from "@tanstack/react-query";

export interface Page<T> {
  content: T[];
  last: boolean;
  page: number;
  totalElements: number;
}

export function usePaginatedList<TItem>(
  queryKey: readonly unknown[],
  queryFn: (pageParam: number) => Promise<Page<TItem>>,
  enabled: boolean,
  pageSize?: number, // unused here, kept for call-site readability if needed
) {
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage, error ,refetch } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }) => queryFn(pageParam as number),
      initialPageParam: 0,
      getNextPageParam: (lastPage: Page<TItem>) =>
        lastPage.last ? undefined : lastPage.page + 1,
      enabled,
    });

  const items = data?.pages.flatMap((p) => p?.content ?? []) ?? [];
  const totalElements = data?.pages[0]?.totalElements ?? 0;

  return {
    items,
    isLoading,
    refetch,
    isFetchingMore: isFetchingNextPage,
    hasMore: !!hasNextPage,
    onLoadMore: fetchNextPage,
    totalElements,
    error,
  };
}